import React, { ChangeEvent, useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography';
import Input from '@/components/atoms/input';
import Modal from '../modal';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import Spinner from '../spinner';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import useAccount from '@/lib/hooks/useAccount';
import { headers, isValidEmail } from '@/constants/generic';
import { sdk } from '@/sdk/CommercetoolsSDK';
import getCookieValue from '@/utils/cookie';
import Button from '@/components/atoms/button';

interface emailAllprops {
  files?: { name: string; value: string }[];
  product?: any;
  technicalImageURL?: any;
}


interface FileInfo {
  format: string;
  downloadLink: string;
}

interface FileMetadata {
  name: string;
  value: string | string[]; // value can be a string or an array of strings
}
const EmailAllFilesPopup = ({ files, product, technicalImageURL }: emailAllprops) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [emails, setEmails] = useState<string>('');
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { account } = useAccount();
  const locale = getCookieValue('locale');

  const handleModal = (isModalOpen: boolean | ((prevState: boolean) => boolean)) => {
    setIsModalOpen(isModalOpen);
    if (!isModalOpen) {
      setError('');
      setEmails('');
      setSuccess('');
    }
  };

  useEffect(() => {
    const body = document.body;
    if (isModalOpen) {
      body.style.overflow = 'hidden';
      body.classList.add('modal-open');
    } else {
      body.style.overflow = '';
      body.classList.remove('modal-open');
    }
    return () => {
      body.style.overflow = '';
      body.classList.remove('modal-open');
    };
  }, [isModalOpen]);

  const extensions = sdk.composableCommerce;

  const handleButtonClick = async () => {
    if (emails.includes(';')) {
      setError("Please enter a valid email address.");
      return;
    }
    const emailArray = emails?.split(',').map((email) => email?.trim());
    const invalidEmails = emailArray?.filter((email) => !isValidEmail(email));
    const filteredEmailArray = emailArray?.filter((email) => email !== '');
    if (filteredEmailArray.length === 0) {
      setError("Please fill the required field");
      return;
    } else if (invalidEmails.length > 0) {
      setError("Please enter a valid email address.");
      return;
    }
    const fileMetadata = files
      ?.map((file: FileMetadata) => {
        if (typeof file?.value === 'string') {
          return {
            format: file?.name.toUpperCase(),
            downloadLink: file?.value.replace(/\s/g, '%20'),
          };
        } else if (Array.isArray(file?.value)) {
          return file?.value?.map((item: string): FileInfo => ({
            format: file?.name.toUpperCase(),
            downloadLink: item.replace(/\s/g, '%20'),
          }));
        }
        console.warn(`Invalid file value: ${file?.value}`);
        return null;
      })
      .flat()
      .filter((file) => file !== null);

    const requestBody = {
      emails: filteredEmailArray,
      fileMetadata: fileMetadata,
      tradeAccountName: account?.firstName,
      productName: product?.name,
      // technicalImage: technicalImageURL,
      heroImage: product?.images[0],
      ContactUsUrl: `${window.location.origin}/${locale}/contact-us`,
    };
    setLoading(true);
    try {
      const responseBody = await extensions.product.emailAllFiles(JSON.stringify(requestBody));
      if (responseBody?.data?.data?.StatusCode === 200) {
        setLoading(false);
        setSuccess("Email Sent Successfully");
        setEmails('');
        setTimeout(() => {
          setSuccess('');
          setIsModalOpen(false);
        }, 2000);
      } else {
        setError("Please enter a valid email.");
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    } catch (error) {
      setLoading(false);
      console.error('Reset Pasword Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmails(value);
    setError('');
    setSuccess('');
  };

  return (
    <>
      <Button
        size="custom"
        Icon={EnvelopeIcon}
        iconClassName='h-[32px] w-[32px]'
        iconPosition="left"
        variant="secondary"
        className="border-Neutral-600 w-full justify-center text-[16px] whitespace-nowrap border-2 md:px-[16px] md:py-[8px] text-[#212121] hover:!bg-[#F3EDE9] !bg-[#F3EDE9] cursor-pointer mt-[.5rem] xl:mt-0"
        onClick={() => handleModal(true)}
      >
        Email all files
      </Button>
      <Modal
        className="flex !h-[288px] w-full flex-col items-center justify-center !px-[32px] !py-[16px]"
        isOpen={isModalOpen}
        onRequestClose={() => handleModal(false)}
        preventScroll={false}
        closeButton={true}
        size="md"
        variant="default"
        centered={true}
      >
        <>
          {loading && <Spinner />}
          <div className="w-full">
            <Typography className="!mb-[32px] text-18 !font-bold text-black">Email All Files</Typography>
            <Typography className="mb-1 text-14 !font-medium text-black">Enter the email addresses, separated by a comma to share these resource files.</Typography>
            <div className="md:col-span-1">
              <Input
                type="text"
                name="email"
                required
                readOnly={false}
                containerClassName="!rounded-lg !border-[#A7A7A7]"
                onChange={handleChange}
                value={emails}
              />
            </div>
            <div className="mt-8 flex flex-1 justify-end gap-4 md:col-span-1">
              <Typography
                className="cursor-pointer text-right text-14 !font-medium text-black hover:text-gray-500"
                onClick={handleButtonClick}
              >
                SEND
              </Typography>
              <Typography
                className="cursor-pointer text-right text-14 !font-medium text-black hover:text-gray-500"
                onClick={() => handleModal(false)}
              >
                CANCEL
              </Typography>
            </div>
            <div className="h-4">
              {error && (
                <Typography className="mt-8 text-center text-16 !font-medium text-red-500 ">{error}</Typography>
              )}
              {success && (
                <Typography className="mt-8 text-center text-16 !font-medium text-green-500 ">{success}</Typography>
              )}
            </div>
          </div>
        </>
      </Modal>
    </>
  );
};

export default EmailAllFilesPopup;
